import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { Airport } from "../shared/models/airport.model";
import { Flight } from "../shared/models/flight.model";
import { Helicopter } from "../shared/models/helicopter.model";
import { Pilot } from "../shared/models/pilot.model";
import moment from "moment";

export class FlightAttribute {
    id: number;
    name: string;
    description: string;
}

export class CalendarFlight {
    departureDate: string;
    flights: Flight[];
}

enum EndpointRoutes {
    GET_PILOT = "pilot/get",
    GET_HELICOPTER = "helicopter/get",
    GET_FLIGHT_ATTRIBUTES = "flight/attribute/get",
    GET_AIRPORTS = "airport/get",
    INSERT_FLIGHT = "flight/insert",
    UPDATE_FLIGHT = "flight/update",
    GET_FLIGHTS = "flights/get",
    EXPORT_FLIGHTS = "flights/export",
    GET_FLIGHT = "flight/get",
    SEARCH_FLIGHT = "flight/search",
    DELETE_FLIGHT = "flight/delete",
    GET_MASS_BALANCE = "massBalance/get",
    GET_HELICOPTER_FLIGHTS = "helicopter/flights/get",
    UPDATE_FLIGHT_LOCATIONS = "flights/updateLocations",
    EXPORT_FILTERS_SAVE = "exportFilters/save",
    EXPORT_FILTERS_DELETE = "exportFilters/delete",
}

@Injectable({
    providedIn: "root",
})
export class FlightsService {
    private _flights: Flight[];
    private _calendarFlights: CalendarFlight[] = [];
    private _pilots: Pilot[] = [];
    private _helicopters: Helicopter[] = [];
    private _flightAttributes: { flightOperations: FlightAttribute[]; flightTypes: FlightAttribute[] };
    private _airports: Airport[] = [];

    public flightsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public flightInserted: BehaviorSubject<Flight> = new BehaviorSubject<Flight>(null);
    public onFlightsChange: Subject<void> = new Subject<void>();
    public openUpdateChildFlightDialog: Subject<{ parentFlightCode: string; parentFlightId: number }> = new Subject<{ parentFlightCode: string; parentFlightId: number }>();
    public parentFlightColors: { flightId: number; color: string }[] = [];

    constructor(private _http: HttpClient) {}

    public getFlights(): Flight[] {
        return this._flights.slice();
    }

    public getPilots() {
        return this._pilots.slice();
    }

    public getHelicopters() {
        return this._helicopters.slice();
    }

    public getFlightAttributesArray() {
        return this._flightAttributes;
    }

    public getAirportsArray() {
        return this._airports.slice();
    }

    public getCalendarFlightsArray() {
        return this._calendarFlights.slice();
    }

    private _deleteFlightFromArray(flightId: number): void {
        const flight = this._flights.filter((f) => f.id === flightId)[0];
        const calendarFlights = this._calendarFlights.filter((cf) => cf.departureDate === flight.flightDate)[0].flights;
        const index = this._flights.indexOf(flight);
        this._flights.splice(index, 1);
        const calendarFlightsIndex = this._flights.indexOf(flight);
        calendarFlights.splice(calendarFlightsIndex, 1);
    }

    public getPilotesByOrgId(orgId: string) {
        let params = new HttpParams();
        params = params.append("organizationId", orgId);
        return this._http.get<Pilot[]>(environment.serverUrl + EndpointRoutes.GET_PILOT, { params: params }).pipe(
            map((responseData) => {
                this._pilots = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHelicoptersByOrgId(orgId: string) {
        let params = new HttpParams();
        params = params.append("organizationId", orgId);
        return this._http.get<Helicopter[]>(environment.serverUrl + EndpointRoutes.GET_HELICOPTER, { params: params }).pipe(
            map((responseData) => {
                this._helicopters = responseData.filter((h) => h.status !== 2);
                return responseData.filter((h) => h.status !== 2);
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getHelicopterFlights(startDate: string, endDate: string, helicopterId: string): Observable<any> {
        this.flightsLoading.next(true);
        let params = new HttpParams();
        params = params.append("startDate", startDate);
        params = params.append("endDate", endDate);
        if (helicopterId) {
            params = params.append("helicopterId", helicopterId);
        }
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.GET_HELICOPTER_FLIGHTS, { params: params }).pipe(
            map((responseData) => {
                this.flightsLoading.next(false);
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getDailyFlights(startDate: string, endDate: string, pilotId: string, helicopterId: string, type: string, showPositioningFlights: boolean): Observable<any> {
        this.flightsLoading.next(true);
        let params = new HttpParams();
        params = params.append("startDate", startDate);
        params = params.append("endDate", endDate);
        if (pilotId) {
            params = params.append("pilotId", pilotId);
        }
        if (helicopterId) {
            params = params.append("helicopterId", helicopterId);
        }
        if (type) {
            params = params.append("type", type);
        }
        return this._http.get<any[]>(environment.serverUrl + EndpointRoutes.GET_FLIGHTS, { params: params }).pipe(
            map((responseData) => {
                this._flights = [];
                this._calendarFlights = [];
                const data = showPositioningFlights ? responseData : responseData.filter((r) => r.isPositioning === 0);
                console.log(data);
                data.map((flight) => {
                    const calendarFlightObject = this._calendarFlights.filter((c) => c.departureDate === flight.flightDate)[0];
                    if (!calendarFlightObject) {
                        this._calendarFlights.push({
                            departureDate: flight.flightDate,
                            flights: [flight],
                        });
                    } else {
                        calendarFlightObject.flights.push(flight);
                    }
                });
                this._flights = responseData;
                this.flightsLoading.next(false);
                return this._calendarFlights;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getFlightById(flightId: string): Observable<Flight> {
        let params = new HttpParams();
        params = params.append("id", flightId);
        return this._http.get<Flight>(environment.serverUrl + EndpointRoutes.GET_FLIGHT, { params: params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getMassBalanceByFlightId(flightId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append("flightId", flightId.toString());
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.GET_MASS_BALANCE, { params: params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getFlightAttributes() {
        return this._http.get<{ flightOperations: FlightAttribute[]; flightTypes: FlightAttribute[] }>(environment.serverUrl + EndpointRoutes.GET_FLIGHT_ATTRIBUTES).pipe(
            map((responseData) => {
                this._flightAttributes = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public getAirports() {
        return this._http.get<any>(environment.serverUrl + EndpointRoutes.GET_AIRPORTS).pipe(
            map((responseData) => {
                this._airports = responseData;
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public searchAirports(filter: { name: string } = { name: "" }): Observable<Airport[]> {
        let params = new HttpParams();
        params = params.append("name", filter.name);
        return this._http.get<Airport[]>(environment.serverUrl + EndpointRoutes.GET_AIRPORTS, { params }).pipe(
            tap((response: Airport[]) => {
                return response;
            }),
        );
    }

    public createNewFlight(flight: Flight): Observable<Flight> {
        return this._http.put<Flight>(environment.serverUrl + EndpointRoutes.INSERT_FLIGHT, { flight: flight }).pipe(
            map((responseData) => {
                // TODO
                this._flights.push(responseData);
                const calendarFlightObject = this._calendarFlights.filter((cf) => cf.departureDate === responseData.flightDate)[0];
                if (calendarFlightObject) {
                    calendarFlightObject.flights.push(responseData);
                } else {
                    this._calendarFlights.push({
                        departureDate: responseData.flightDate,
                        flights: [responseData],
                    });
                }
                this.flightInserted.next(responseData);
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateFlight(flight: Flight): Observable<Flight> {
        return this._http.post<Flight>(environment.serverUrl + EndpointRoutes.UPDATE_FLIGHT, { flight: flight }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public updateFlightLocations(flights: any): Observable<any> {
        return this._http.put<any>(environment.serverUrl + EndpointRoutes.UPDATE_FLIGHT_LOCATIONS, { flights: flights }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public searchFlightsByCode(filter: { name: string } = { name: "" }): Observable<{ id: number; code: string }[]> {
        let params = new HttpParams();
        params = params.append("flightCode", filter.name);
        return this._http.get<{ id: number; code: string }[]>(environment.serverUrl + EndpointRoutes.SEARCH_FLIGHT, { params }).pipe(
            tap((response: { id: number; code: string }[]) => {
                return response;
            }),
        );
    }

    public softDeleteById(flightId: number): Observable<any> {
        let params = new HttpParams();
        params = params.append("id", flightId.toString());
        return this._http.delete<any>(environment.serverUrl + EndpointRoutes.DELETE_FLIGHT, { params: params }).pipe(
            map((responseData) => {
                this._deleteFlightFromArray(flightId);
                this.onFlightsChange.next();
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public saveExportPresetFilter(organizationId: number, filterName: string, visibleColumns: any[], id?: number): Observable<any> {
        return this._http.put(environment.serverUrl + EndpointRoutes.EXPORT_FILTERS_SAVE, { organizationId, filterName, visibleColumns, id }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public deleteExportPresetFilter(id: number): Observable<any> {
        let params = new HttpParams();
        params = params.append("id", id.toString());
        return this._http.delete(environment.serverUrl + EndpointRoutes.EXPORT_FILTERS_DELETE, { params: params }).pipe(
            map((responseData) => {
                return responseData;
            }),
            catchError((errorRes) => {
                return throwError(errorRes);
            }),
        );
    }

    public exportFlights(hId: any, blockId: number, startDate?: string, endDate?: string): Observable<any> {
        return this._http
            .post(environment.serverUrl + EndpointRoutes.EXPORT_FLIGHTS, {
                blockId: blockId < 0 ? undefined : blockId,
                helicopterId: hId,
                startDate: startDate,
                endDate: endDate,
            })
            .pipe(
                map((res: any) => {
                    res.forEach((r) => {
                        r.flightDate = {
                            date: new Date(r.flightDate),
                            year: this.splitDate(r.flightDate, "year"),
                            month: this.splitDate(r.flightDate, "month"),
                            day: this.splitDate(r.flightDate, "day"),
                        };
                        r.penality = r.penality ? "Yes" : "No";
                        r.isPositioning = r.isPositioning ? "Yes" : "No";
                        r.startStopTimeMin = this.toMinutes(r.startStopTime);
                        r.startStopTime = this.toHmm(r.startStopTime);
                        r.flightTimeMin = this.toMinutes(r.flightTime);
                        r.flightTime = this.toHmm(r.flightTime);
                        if (r.helicopter.airframeActualTotFh) {
                            r.helicopter.airframeActualTotFh = this.toHmm(r.helicopter.airframeActualTotFh);
                        }
                        if (r.helicopter.engine1TotFH) {
                            r.helicopter.engine1TotFH = this.toHmm(r.helicopter.engine1TotFH);
                        }
                        if (r.helicopter.engine2TotFH) {
                            r.helicopter.engine2TotFH = this.toHmm(r.helicopter.engine2TotFH);
                        }
                        r.fuelArrivalUnit = this.toUnit(r.fuelArrival);
                        r.fuelArrival = this.toValue(r.fuelArrival);
                        r.refuelUnit = this.toUnit(r.refuel);
                        r.refuel = this.toValue(r.refuel);
                        r.totalFuelUnit = this.toUnit(r.totalFuel);
                        r.totalFuel = this.toValue(r.totalFuel);
                        r.refuelBeforeFirstFlightUnit = this.toUnit(r.refuelBeforeFirstFlight);
                        r.refuelBeforeFirstFlight = this.toValue(r.refuelBeforeFirstFlight);
                        r.totalFuelBeforeNextFlightUnit = this.toUnit(r.totalFuelBeforeNextFlight);
                        r.totalFuelBeforeNextFlight = this.toValue(r.totalFuelBeforeNextFlight);
                        if (r.defectReports?.length > 0) {
                            r.defect = r.defectReports
                                .filter((d) => d.defect !== "")
                                .map((d) => {
                                    if (!d.defectDate) {
                                        if (d.defectType !== 0) {
                                            if (d.byTechnician) {
                                                return (d.defectType === 1 ? "(TD) " : "(TR) ") + d.defect;
                                            }
                                            return (d.defectType === 1 ? "(PD) " : "(PR) ") + d.defect;
                                        }
                                        return d.defect;
                                    }
                                    if (d.defectType !== 0) {
                                        if (d.byTechnician) {
                                            return (d.defectType === 1 ? "(TD) " : "(TR) ") + d.defect + " (" + moment(d.defectDate).format("DD-MM-YYYY") + ")";
                                        }
                                        return (d.defectType === 1 ? "(PD) " : "(PR) ") + d.defect + " (" + moment(d.defectDate).format("DD-MM-YYYY") + ")";
                                    }
                                    return d.defect + " (" + moment(d.defectDate).format("DD-MM-YYYY") + ")";
                                })
                                .join("<br>");
                            r.actionTaken = r.actionTakenReports
                                .filter((a) => a.actionTaken !== "")
                                .map((a) => {
                                    if (!a.actionTakenDate) {
                                        if (a.actionTakenType !== 0) {
                                            if (a.byTechnician) {
                                                return (a.actionTakenType === 1 ? "(TD) " : "(TR) ") + a.actionTaken;
                                            }
                                            return (a.actionTakenType === 1 ? "(PA) " : "(PR) ") + a.actionTaken;
                                        }
                                        return a.actionTaken;
                                    }
                                    if (a.actionTakenType !== 0) {
                                        if (a.byTechnician) {
                                            return (a.actionTakenType === 1 ? "(TA) " : "(TR) ") + a.actionTaken + " (" + moment(a.actionTakenDate).format("DD-MM-YYYY") + ")";
                                        }
                                        return (a.actionTakenType === 1 ? "(PA) " : "(PR) ") + a.actionTaken + " (" + moment(a.actionTakenDate).format("DD-MM-YYYY") + ")";
                                    }
                                    return a.actionTaken + " (" + moment(a.actionTakenDate).format("DD-MM-YYYY") + ")";
                                })
                                .join("<br>");
                        }
                        r.atlRef = r.blockNumber + "/" + r.documentNumber;
                    });
                    return res;
                }),
            );
    }

    private toValue(data: any): number | string | null {
        if (!data) {
            return null;
        }
        const regex = /([\d\.]+)/;

        const pipeIndex = data.toString().indexOf("|");

        if (pipeIndex > 0) {
            const matches1 = data.substring(0, pipeIndex).match(regex);
            const matches2 = data.substring(pipeIndex + 1, data.length).match(regex);

            if (matches1 && matches2) {
                if (matches1[0] === matches2[0]) {
                    return parseFloat(matches1[0]);
                } else {
                    return matches1[0] + "|" + matches2[0];
                }
            }
        } else {
            const matches = data.toString().match(regex);
            if (matches) {
                return parseFloat(matches[1]);
            }
        }
        return null;
    }

    private toUnit(data: any): string | null {
        if (!data) {
            return null;
        }

        const regex = /([^\d+\.\ ]+)/;

        const pipeIndex = data.toString().indexOf("|");

        if (pipeIndex > 0) {
            const matches1 = data.substring(0, pipeIndex).match(regex);
            const matches2 = data.substring(pipeIndex + 1, data.length).match(regex);

            if (matches1 && matches2) {
                if (matches1[0] === matches2[0]) {
                    return matches1[0];
                } else {
                    return matches1[0] + "|" + matches2[0];
                }
            }
        } else {
            const matches = data.toString().match(regex);
            if (matches) {
                return matches[1];
            }
        }

        return null;
    }

    private toMinutes(time: any) {
        try {
            if (!time || time.length === 0) {
                return time;
            }

            const pipeIndex = time.indexOf("|");
            if (pipeIndex > 0) {
                time = time.substring(pipeIndex + 1, time.length);
            }
            const parsedTime = moment(time, "HH:mm");
            return parsedTime.hours() * 60 + parsedTime.minutes();
        } catch (e) {
            return time;
        }
    }

    private toHmm(time: string) {
        if (!time) return "";
        if (!time.includes(":")) return time;
        if (time.includes("|")) return time;
        let hours = time.split(":")[0];
        const minutes = time.split(":")[1];
        if (hours[0] === "0") {
            hours = hours.slice(1);
        }
        return hours + ":" + minutes;
    }

    private splitDate(date: string, type: "year" | "month" | "day"): number {
        let res = null;
        switch (type) {
            case "year":
                res = date.split("-")[0];
                break;
            case "month":
                res = date.split("-")[1];
                break;
            case "day":
                res = date.split("-")[2];
                break;
        }
        return Number(res);
    }
}
